import TextField from '@components/v2/form/TextField/TextField';
import Icon from '@components/v2/ui/Icon/Icon';
import { InputAdornment } from '@mui/material';
import lodash from 'lodash';
import { Search } from 'lucide-react';
import * as R from 'ramda';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useListPick } from '../../components/ListPicker/useListPick';
import AutocompleteMuiAdapter from '../../components/adaptersMui/AutocompleteMuiAdapter/AutocompleteMuiAdapter';
import { DataQueryKey } from '../../hooks/useDataQuery/dataQuery.types';
import LangEnum from '../../lang/LangEnum';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import { onScrollEndReload } from '../../utils/generics';
import { ActiveOrderTestIds } from '../../utils/testIds/ActiveOrder.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { Customer, CustomerAutocomplete } from '../customer/types/customer.types';
import { useNetworkContext } from '../offline/contexts/network/network.context';
import { customersRepositoryOfflineAdapters } from '../offline/offlineRequestAdapters/customers.repository.offlineAdapters';
import { createParamsToUseListPickWhenOffline } from '../offline/utils/createParamsToUseListPickWhenOffline';
import useCustomerStatusToCreateOrder from '../orders/hooks/useCustomerStatusToCreateOrder';
import useProductDetailsActions from '../products/products-details-v2/hooks/useProductsDetailsActions';
import { useTranslation } from '../translation/TranslationContext';
import OrderCustomerOptionRenderer from './OrderCustomerOptionRenderer';

type OrderCustomerAutocompleteProps = {
  disabled?: boolean;
  disableClearable?: boolean;
  isSuggested?: boolean;
  onChangeCustomer?: (customer: Customer) => void;
};

const OrderCustomerAutocomplete = ({
  disabled,
  disableClearable,
  isSuggested,
}: OrderCustomerAutocompleteProps) => {
  const { isOnline } = useNetworkContext();
  const { verifyProductDetailsModalOpenedByOrderBook } = useProductDetailsActions();
  const { selectedOrder, createOrder } = useOrderBookStore(
    useShallow((state) => ({ selectedOrder: state.selectedOrder, createOrder: state.createOrder })),
  );
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const history = useHistory();
  const validCustomerStatusToCreateOrder = useCustomerStatusToCreateOrder();

  const [inputText, setInputText] = useState('');

  const listPickActions = useListPick<CustomerAutocomplete>(
    createParamsToUseListPickWhenOffline({
      isOnline,
      offlineFetcher: customersRepositoryOfflineAdapters.page,
      onlineProps: {
        url: DataQueryKey.CUSTOMERS_FILTER_OPTIONS,
        order: { commercialName: 'ASC' },
        defaultQuery: {
          status: validCustomerStatusToCreateOrder,
          customerAddress: true,
        },
        queryField: 'query',
        limit: 20,
      },
    }),
  );

  const onChangeInput = (value: string) => {
    listPickActions.onSearch(value);
  };

  const debouncedInputChange = useRef(
    lodash.debounce((value: string) => {
      onChangeInput(value);
    }, 500),
  ).current;

  const handleChangeInput = (_: React.ChangeEvent<{}>, value: string) => {
    setInputText(value);
    debouncedInputChange(value);
  };

  const customers: CustomerAutocomplete[] | false =
    listPickActions && R.uniqBy(({ id }) => id, listPickActions.leftItems);

  return (
    <AutocompleteMuiAdapter
      key={selectedOrder?.id}
      disabled={disabled}
      onChange={(_: any, selectedCustomer: CustomerAutocomplete | null) => {
        if (selectedCustomer) {
          createOrder({
            history,
            team,
            customerId: selectedCustomer?.id,
            translate,
            onSuccess: verifyProductDetailsModalOpenedByOrderBook,
            isSuggested,
          });
        }
      }}
      clearOnBlur={false}
      filterOptions={(options) => options}
      inputValue={inputText}
      onInputChange={handleChangeInput}
      loading={listPickActions && listPickActions.loading}
      options={(listPickActions && customers) || []}
      noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
      loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
      getOptionLabel={(item: CustomerAutocomplete) => item.commercialName}
      getOptionKey={(item: CustomerAutocomplete) => item.id}
      renderOption={(option: CustomerAutocomplete) => (
        <OrderCustomerOptionRenderer customer={option} />
      )}
      slotProps={{
        listbox: {
          role: 'list-box',
          style: {
            maxHeight: 248,
          },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      test-id={ActiveOrderTestIds.ORDER_BOOK_CUSTOMERS_AUTOCOMPLETE}
      disableClearable={disableClearable}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          autoFocus
          id={params.id}
          size="medium"
          disabled={params.disabled}
          placeholder={translate(LangEnum.GLOBAL_SEARCH_STORE)}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={Search} size="md" />
                </InputAdornment>
              ),
            },
            htmlInput: params.inputProps,
          }}
        />
      )}
    />
  );
};

export default OrderCustomerAutocomplete;
